import { FC, PropsWithChildren, useMemo } from 'react';
import { constants } from '@oresundsbron/validators';
import { DefaultLink } from './DefaultLink';
import { LinkProps } from './types';
import { useLinksV2 } from '~/hooks/useLinksV2';
import { useLocale } from '~/stores/locale';

type ContractTypes =
  | typeof constants.OresundGo
  | typeof constants.OresundBusiness
  | typeof constants.OresundBusinessPendler
  | typeof constants.Storebelt
  | typeof constants.OresundPendler
  | 'online_ticket';

const storebeltLinks: Record<'sv' | 'da' | 'en', string> = {
  sv: 'https://storebaelt.dk/en/terms-and-conditions/',
  da: 'https://storebaelt.dk/vilkar-og-betingelser/',
  en: 'https://storebaelt.dk/en/terms-and-conditions/',
};

export type TermsLinkProps = Omit<LinkProps, 'href'> & {
  type: ContractTypes;
  className?: string;
  isOresundPay?: boolean;
};

export const TermsLink: FC<PropsWithChildren<TermsLinkProps>> = ({
  type,
  children,
  isOresundPay,
  ...rest
}) => {
  const { findLinkByCodeRef } = useLinksV2();
  const { locale } = useLocale();
  const href = useMemo(() => {
    const getCodeRefForType = (type: ContractTypes): string => {
      if (isOresundPay) {
        if (type === constants.OresundGo || type === constants.OresundPendler) {
          return 'terms_and_conditions_private_oresundpay';
        }
        return 'terms_and_conditions_business_oresundpay';
      }

      if (type === constants.OresundGo || type === constants.OresundPendler) {
        return 'terms_and_conditions_private';
      }

      if (
        type === constants.OresundBusiness ||
        type === constants.OresundBusinessPendler
      ) {
        return 'terms_and_conditions_business';
      }

      return 'terms_and_conditions_online_ticket';
    };

    if (type === constants.Storebelt) {
      return storebeltLinks[locale as 'sv' | 'da' | 'en'];
    }

    const link = findLinkByCodeRef(getCodeRefForType(type));
    return link ? `/${link.locale}${link.path}` : undefined;
  }, [findLinkByCodeRef, isOresundPay, locale, type]);

  return (
    <>
      {href && (
        <DefaultLink {...rest} href={href} target="_blank">
          {children}
        </DefaultLink>
      )}
    </>
  );
};
